<script lang="ts" setup>
import { computed, ref, onMounted, watch } from 'vue'
import draggable from 'vuedraggable'
import { ICareerResponse, Revoe, Plane } from '@shared/types/base'
import { useStore } from 'vuex'
import { apiService } from '@/services/apiService'
import { getInstitutionCarreras } from '@/components/utils/functions'
import { formatDate } from '@/utils/dateFormatter'

const store = useStore()
const props = defineProps<{
	carreas: ICareerResponse[]
}>()

const emit = defineEmits(['update:showSuccessModal', 'update:showErrorModal'])

// Copia local de las carreras donde se aplicarán los cambios
const carreasLocal = ref<{ [key: string]: ICareerResponse }>({})

// Carrera seleccionada por el usuario
const carreraSeleccionada = ref<string | null>(null)

/**
 * @description Realiza una copia profunda de las carreras
 */
const deepCopyCarreras = (carreras: { [key: string]: ICareerResponse }): { [key: string]: ICareerResponse } => structuredClone(carreras)

/**
 * @description Inicializa la copia local de las carreras
 */
const inicializarCarrerasLocal = () => {
	getInstitutionCarreras(store.getters['global/currentInstitution']).then(carreras => {
		carreasLocal.value = deepCopyCarreras(carreras)

		// Verificar si la carrera seleccionada existe en las nuevas carreras
		const carreraExiste = carreraSeleccionada.value ? Object.values(carreasLocal.value).some(c => String(c.idCarrera) === String(carreraSeleccionada.value)) : false

		// Si no existe o no hay selección, seleccionar la primera carrera
		if (!carreraExiste && Object.keys(carreasLocal.value).length > 0) {
			const primeraCarrera = Object.values(carreasLocal.value)[0]
			carreraSeleccionada.value = primeraCarrera.idCarrera
		} else if (!carreraExiste) {
			// Si no hay carreras disponibles, limpiar la selección
			carreraSeleccionada.value = null
		}
	})
}

// Inicializar cuando se monta el componente
onMounted(() => {
	inicializarCarrerasLocal()
})

// Observar cambios en la institución actual para resetear la selección
watch(
	() => store.getters['global/currentInstitution'],
	() => {
		// Cuando cambia la institución, resetear la selección y recargar carreras
		carreraSeleccionada.value = null
		inicializarCarrerasLocal()
	}
)

// Observar cambios en la prop para actualizar la copia local si es necesario
watch(
	() => props.carreas,
	() => {
		// Solo actualizar si la prop cambia externamente
		// Puedes comentar esto si no quieres que se sincronice automáticamente
		inicializarCarrerasLocal()
	},
	{ deep: true }
)

/**
 * @description Obtiene las opciones de carreras únicas para el selector
 * Agrega una propiedad calculada para el texto del selector
 */
const opcionesCarreras = computed(() =>
	Object.values(carreasLocal.value).map(carrera => ({
		...carrera,
		displayText: `${carrera.nombreCarrera}`
	}))
)

/**
 * @description Convierte la estructura de carreras en una lista plana para mostrar
 */
const listaCarreras = computed(() => {
	const resultado: {
		carreraKey: string
		carrera: ICareerResponse
		revoeKey: string
		revoe: Revoe
		planKey: string
		plan: Plane
	}[] = []

	// Filtrar por carrera seleccionada si hay una seleccionada
	// Usar carreasLocal en lugar de props.carreas
	const carrerasFiltradas = carreraSeleccionada.value
		? Object.entries(carreasLocal.value).filter(([, c]) => String(c.idCarrera) === String(carreraSeleccionada.value))
		: Object.entries(carreasLocal.value)

	carrerasFiltradas.forEach(([carreraKey, carrera]) => {
		// Iterar sobre cada REVOE
		Object.keys(carrera.Revoes || {}).forEach(revoeKey => {
			const revoe = carrera.Revoes[revoeKey]

			// Iterar sobre cada Plan del REVOE
			Object.keys(revoe.Planes || {}).forEach(planKey => {
				const plan = revoe.Planes[planKey]

				resultado.push({
					carreraKey,
					carrera,
					revoeKey,
					revoe,
					planKey,
					plan
				})
			})
		})
	})

	return resultado
})

/**
 * @description Convierte el objeto Asignaturas en un array ordenado para draggable
 */
const getAsignaturasArray = (asignaturas: any): { key: string; asignatura: any }[] => {
	if (typeof asignaturas !== 'object' || !asignaturas) {
		return []
	}

	const array = Object.keys(asignaturas).map(key => ({
		key,
		asignatura: asignaturas[key]
	}))

	// Ordenar por order si existe, sino por clave
	return array.sort((a, b) => {
		const orderA = a.asignatura.order === undefined ? Infinity : a.asignatura.order
		const orderB = b.asignatura.order === undefined ? Infinity : b.asignatura.order
		if (orderA !== orderB) {
			return orderA - orderB
		}
		return a.key.localeCompare(b.key)
	})
}

/**
 * @description Actualiza el order de las asignaturas después de moverlas
 * Ahora modifica carreasLocal en lugar de la prop
 */
const updateAsignaturasOrder = async (carreraKey: string, revoeKey: string, planKey: string, newOrder: { key: string; asignatura: any }[]) => {
	// Encontrar la carrera en carreasLocal
	const carreraLocal = carreasLocal.value[carreraKey]
	if (!carreraLocal) return

	const plan = carreraLocal.Revoes[revoeKey]?.Planes[planKey]
	if (!plan || !plan.Asignaturas) return

	// Actualizar el order según la nueva posición (empezando desde 1)
	newOrder.forEach((item, index) => {
		if (plan.Asignaturas[item.key]) {
			plan.Asignaturas[item.key].order = index + 1
		}
	})
}

/**
 * @description Helper para manejar el evento de actualización del orden desde el template
 */
const handleOrderUpdate = (item: { carreraKey: string; revoeKey: string; planKey: string }, newOrder: { key: string; asignatura: any }[]) => {
	updateAsignaturasOrder(item.carreraKey, item.revoeKey, item.planKey, newOrder)
}

/**
 * @description Obtiene el array de asignaturas para un plan específico (computed para reactividad)
 */
const getAsignaturasForPlan = (carrera: ICareerResponse, revoeKey: string, planKey: string) => {
	const plan = carrera.Revoes[revoeKey].Planes[planKey]
	if (!plan || !plan.Asignaturas) {
		return []
	}
	return getAsignaturasArray(plan.Asignaturas)
}
const saveAsignaturasOrder = async () => {
	const institutionId = store.getters['global/currentInstitution']
	await apiService
		.post('saveAsignaturas', {
			institutionId: institutionId,
			Carrera: carreasLocal.value
		})
		.then(() => {
			emit('update:showSuccessModal', { message: 'Orden de asignaturas actualizado correctamente', show: true })
		})
		.catch(error => {
			emit('update:showErrorModal', { message: error.message || 'Error al ordenar las asignaturas', show: true })
		})
}
</script>

<template>
	<va-card class="asignaturas-order-card" bordered>
		<va-card-content class="form-content">
			<!-- Selector de carrera -->
			<div v-if="opcionesCarreras.length > 0" class="career-selector">
				<div class="row">
					<div class="col-md-6">
						<va-select
							v-model="carreraSeleccionada"
							:options="opcionesCarreras"
							value-by="idCarrera"
							text-by="displayText"
							label="Selecciona una carrera"
							placeholder="Elige una carrera para ver sus asignaturas"
							class="career-select"
							style="width: 100%"
							clearable>
							<template #prependInner>
								<va-icon name="school" class="selector-icon" />
							</template>
						</va-select>
					</div>
					<div class="col-md-6 text-right mt-2 d-flex justify-content-end align-items-center">
						<va-button @click="saveAsignaturasOrder()" class="action-button">
							<va-icon name="save" class="button-icon" />
							Guardar cambios
						</va-button>
					</div>
				</div>
			</div>

			<div v-if="listaCarreras.length === 0" class="empty-state">
				<div class="empty-icon-wrapper">
					<va-icon name="info" class="empty-icon" />
				</div>
				<h3 class="empty-title">{{ carreraSeleccionada ? 'No hay datos' : 'Selecciona una carrera' }}</h3>
				<p class="empty-description">
					{{ carreraSeleccionada ? 'No se encontraron datos para la carrera seleccionada' : 'Por favor, selecciona una carrera del selector para comenzar' }}
				</p>
			</div>

			<div v-else class="careers-list">
				<div v-for="item in listaCarreras" :key="`${item.carrera.idCarrera}-${item.revoeKey}-${item.planKey}`" class="career-item">
					<!-- Header compacto horizontal -->
					<div class="career-header-compact">
						<div class="career-icon-wrapper">
							<va-icon name="school" class="career-icon" />
						</div>
						<div class="career-info-compact">
							<div class="info-row">
								<span class="info-label">Carrera:</span>
								<span class="info-value">{{ item.carrera.nombreCarrera }}</span>
								<span class="info-separator">|</span>
								<span class="info-label">Clave:</span>
								<span class="info-value">{{ item.carrera.claveCarrera }}</span>
								<span class="info-separator">|</span>
								<span class="info-label">ID:</span>
								<span class="info-value">{{ item.carrera.idCarrera }}</span>
							</div>
							<div class="info-row">
								<span class="info-label">REVOE:</span>
								<span class="info-value">{{ item.revoe.numero }}</span>
								<span class="info-separator">|</span>
								<span class="info-label">Fecha:</span>
								<span class="info-value">{{ formatDate(item.revoe.fechaExpedicion) }}</span>
								<span class="info-separator">|</span>
								<span class="info-label">Plan:</span>
								<span class="info-value">{{ item.plan.clavePlan }}</span>
								<span class="info-separator">|</span>
								<span class="info-label">Tipo:</span>
								<span class="info-value">{{ item.plan.tipoCarrera }}</span>
								<span class="info-separator">|</span>
								<span class="info-label">Asignaturas:</span>
								<span class="info-value">{{ item.plan.totalAsignaturas }}</span>
								<span class="info-separator">|</span>
								<span class="info-label">Créditos:</span>
								<span class="info-value">{{ item.plan.totalCreditos }}</span>
							</div>
						</div>
					</div>
					<!-- Asignaturas -->
					<div v-if="item.plan.Asignaturas && Object.keys(item.plan.Asignaturas).length > 0" class="asignaturas-section">
						<div class="asignaturas-header">
							<va-icon name="list" class="asignaturas-icon" />
							<span class="asignaturas-title">Asignaturas ({{ Object.keys(item.plan.Asignaturas).length }})</span>
							<span class="asignaturas-hint">
								<va-icon name="drag_indicator" class="hint-icon" />
								Arrastra para ordenar
							</span>
						</div>
						<div class="asignaturas-list">
							<draggable
								:model-value="getAsignaturasForPlan(item.carrera, item.revoeKey, item.planKey)"
								@update:model-value="(newOrder: any) => handleOrderUpdate(item, newOrder)"
								item-key="key"
								handle=".drag-handle"
								class="draggable-list">
								<template #item="{ element }">
									<div class="asignatura-item">
										<div class="drag-handle">
											<va-icon name="drag_indicator" class="drag-icon" />
										</div>
										<div class="asignatura-content">
											<span class="asignatura-name">{{ element.asignatura.nombre }}</span>
											<div class="asignatura-meta">
												<span class="meta-item">Clave: {{ element.asignatura.claveAsignatura }}</span>
												<span class="meta-item">Créditos: {{ element.asignatura.creditos }}</span>
												<span class="meta-item order-badge">Orden: {{ element.asignatura.order !== undefined ? element.asignatura.order : '-' }}</span>
											</div>
										</div>
									</div>
								</template>
							</draggable>
						</div>
					</div>
					<div v-else class="no-asignaturas">
						<va-icon name="info" class="no-asignaturas-icon" />
						<span>No hay asignaturas en este plan</span>
					</div>
				</div>
			</div>
		</va-card-content>
	</va-card>
</template>

<style scoped>
.asignaturas-order-card {
	border-radius: 20px;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
	animation: fadeInUp 0.6s ease-out;
	background: white;
	border: none;
	width: 100%;
	max-width: 100%;
	display: flex;
	flex-direction: column;
	min-height: 0;
}

.form-content {
	padding: 1rem;
	background: #fafbfc;
	flex: 1;
	display: flex;
	flex-direction: column;
	min-height: 0;
	gap: 1.5rem;
}

/* Selector de carrera */
.career-selector {
	background: white;
	border-radius: 12px;
	padding: 1rem;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.career-select {
	width: 100%;
}

.selector-icon {
	color: #062342;
	font-size: 1.25rem;
}

/* Estado vacío */
.empty-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 4rem 2rem;
	text-align: center;
}

.empty-icon-wrapper {
	width: 120px;
	height: 120px;
	border-radius: 50%;
	background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 2rem;
	animation: float 3s ease-in-out infinite;
}

.empty-icon {
	font-size: 4rem;
	color: #062342;
}

.empty-title {
	font-size: 1.75rem;
	font-weight: 700;
	color: #1a202c;
	margin-bottom: 0.75rem;
	letter-spacing: -0.5px;
}

.empty-description {
	color: #718096;
	font-size: 1.125rem;
	font-weight: 500;
}

/* Lista de carreras */
.careers-list {
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
	max-height: calc(100vh - 300px);
	overflow-y: hidden;
	padding: 0.5rem;
}

.career-item {
	background: white;
	border-radius: 12px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
	padding: 1.5rem;
	transition: all 0.3s ease;
	border-left: 4px solid #062342;
}

.career-item:hover {
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
	transform: translateY(-2px);
}

/* Header de carrera compacto horizontal */
.career-header-compact {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	margin-bottom: 1rem;
	padding-bottom: 0.75rem;
	border-bottom: 1px solid #e2e8f0;
}

.career-icon-wrapper {
	width: 36px;
	height: 36px;
	border-radius: 8px;
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	box-shadow: 0 2px 8px rgba(062342, 0.3);
}

.career-info-compact {
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	min-width: 0;
}

.info-row {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	flex-wrap: wrap;
	font-size: 0.875rem;
	line-height: 1.5;
}

.info-label {
	font-weight: 600;
	color: #475569;
	white-space: nowrap;
}

.info-value {
	color: #1a202c;
	font-weight: 500;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 300px;
}

.info-separator {
	color: #cbd5e1;
	margin: 0 0.25rem;
	font-weight: 300;
}

.career-icon {
	color: white;
	font-size: 1.5rem;
}

/* Sección Asignaturas */
.asignaturas-section {
	margin-top: 1rem;
	padding-top: 1rem;
	border-top: 1px solid #e2e8f0;
}

.asignaturas-header {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	margin-bottom: 0.75rem;
}

.asignaturas-icon {
	color: #062342;
	font-size: 1.125rem;
}

.asignaturas-title {
	font-size: 0.9375rem;
	font-weight: 700;
	color: #475569;
	text-transform: uppercase;
	letter-spacing: 0.5px;
}

.asignaturas-hint {
	display: flex;
	align-items: center;
	gap: 0.375rem;
	margin-left: auto;
	font-size: 0.8125rem;
	color: #64748b;
	font-style: italic;
}

.hint-icon {
	font-size: 1rem;
	color: #062342;
}

.asignaturas-list {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	max-height: 520px;
	overflow-y: auto;
	padding: 1.5rem;
	background: #f8f9fa;
	border-radius: 8px;
}

.draggable-list {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.asignatura-item {
	background: white;
	border-radius: 8px;
	padding: 0.75rem 1rem;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
	transition: all 0.2s ease;
	border-left: 3px solid #062342;
	display: flex;
	align-items: center;
	gap: 0.75rem;
	cursor: move;
}

.asignatura-item:hover {
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	transform: translateX(4px);
}

.drag-handle {
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: grab;
	color: #062342;
	transition: all 0.2s ease;
	flex-shrink: 0;
}

.drag-handle:active {
	cursor: grabbing;
}

.drag-handle:hover {
	color: #062342;
	transform: scale(1.1);
}

.drag-icon {
	font-size: 1.25rem;
}

.asignatura-content {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.asignatura-name {
	font-size: 0.9375rem;
	font-weight: 600;
	color: #1a202c;
}

.asignatura-meta {
	display: flex;
	gap: 0.75rem;
	flex-wrap: wrap;
}

.meta-item {
	font-size: 0.8125rem;
	color: #64748b;
	font-weight: 500;
}

.order-badge {
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	padding: 0.25rem 0.5rem;
	border-radius: 4px;
	font-weight: 700;
	color: #ffffff;
}

.no-asignaturas {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding: 1rem;
	background: #f8f9fa;
	border-radius: 8px;
	color: #475569;
	font-size: 0.875rem;
	font-style: italic;
}

.no-asignaturas-icon {
	font-size: 1.125rem;
}

/* Animaciones */
@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes float {
	0%,
	100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-10px);
	}
}

/* Scrollbar personalizado */
.careers-list::-webkit-scrollbar,
.asignaturas-list::-webkit-scrollbar {
	width: 8px;
	height: 8px;
}

.careers-list::-webkit-scrollbar-track,
.asignaturas-list::-webkit-scrollbar-track {
	background: #f1f1f1;
	border-radius: 4px;
}

.careers-list::-webkit-scrollbar-thumb,
.asignaturas-list::-webkit-scrollbar-thumb {
	background: #888;
	border-radius: 4px;
}

.careers-list::-webkit-scrollbar-thumb:hover,
.asignaturas-list::-webkit-scrollbar-thumb:hover {
	background: #555;
}

@media (max-width: 768px) {
	.form-content {
		padding: 1.5rem;
	}
}

@media (max-width: 480px) {
	.form-content {
		padding: 1rem;
	}
}
</style>
